import styled from 'styled-components';
import { Chat } from '../Chat';
import { Background } from './Background';
import { CharacterSection } from './CharacterSection';
import { SocialLinks } from '../ui/SocialLinks';
import { Navigation } from '../ui/Navigation';
import { theme } from '../../styles/theme';
import { useAccount } from '../WalletProvider';
import { useWalletModal } from '@solana/wallet-adapter-react-ui';
import { Link } from 'react-router-dom';

const LayoutContainer = styled.div`
  position: relative;
  width: 100%;
  height: 100%;
  display: grid;
  grid-template-columns: 1fr 1fr;
  padding: ${theme.spacing.lg};
  gap: ${theme.spacing.xl};
  z-index: ${theme.zIndex.chat};
`;

const TopBar = styled.div`
  position: absolute;
  top: ${theme.spacing.lg};
  width: 100%;
  padding: 0 ${theme.spacing.lg};
  display: flex;
  align-items: center;
  justify-content: space-between;
  z-index: ${theme.zIndex.chat + 1};
`;

const Logo = styled.img`
  height: 120px;
  transition: transform 0.2s;

  &:hover {
    transform: scale(1.05);
  }
`;

const TopBarRight = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
`;

const SocialIconsGroup = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  padding-right: 16px;
  border-right: 1px solid rgba(0, 0, 0, 0.1);
`;

const InteractiveSection = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  height: 100%;
`;

const CharacterArea = styled.div`
  position: relative;
  height: 100%;
  display: flex;
  align-items: flex-end;
`;

const WelcomeMessage = styled.div`
  font-family: 'M PLUS Rounded 1c', sans-serif;
  font-size: 1.4rem;
  line-height: 1.6;
  color: rgba(0, 0, 0, 0.8);
  background: rgba(255, 255, 255, 0.95);
  padding: ${theme.spacing.xl};
  border-radius: 32px;
  box-shadow: 
    0 4px 24px rgba(0, 0, 0, 0.08),
    0 1px 2px rgba(0, 0, 0, 0.04);
  max-width: 380px;
  margin: -100px 20px 0 auto;
  text-align: center;
  animation: floatIn 0.6s ease-out;
  position: relative;

  /* Speech bubble tail */
  &::after {
    content: '';
    position: absolute;
    right: -20px;
    top: 50%;
    transform: translateY(-50%);
    border-style: solid;
    border-width: 20px 0 20px 20px;
    border-color: transparent transparent transparent rgba(255, 255, 255, 0.95);
  }

  @keyframes floatIn {
    from {
      opacity: 0;
      transform: translateY(20px);
    }
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }

  span {
    color: #FFB7C5;
    font-weight: 700;
  }
`;

const WalletLink = styled.button`
  background: linear-gradient(135deg, #FFB7C5 0%, #ff9eb1 100%);
  border: none;
  padding: 8px 16px;
  border-radius: 20px;
  font: inherit;
  color: #FFFFFF;
  font-weight: 700;
  cursor: pointer;
  transition: all ${theme.transitions.fast};
  box-shadow: 0 2px 8px rgba(255, 183, 197, 0.3);

  &:hover {
    transform: translateY(-1px);
    box-shadow: 0 4px 12px rgba(255, 183, 197, 0.4);
    background: linear-gradient(135deg, #ff9eb1 0%, #ff85a1 100%);
  }
`;

const HeroPointer = styled.div`
  position: absolute;
  right: -40px;
  top: 50%;
  transform: translateY(-50%);
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 12px;
  z-index: ${theme.zIndex.chat + 1};
`;

const ArrowImage = styled.img`
  width: 100px;
  height: auto;
  animation: bounce 2s infinite;
  filter: drop-shadow(2px 2px 4px rgba(0, 0, 0, 0.15));

  @keyframes bounce {
    0%, 100% {
      transform: translateX(0);
    }
    50% {
      transform: translateX(5px);
    }
  }
`;

const HeroText = styled.div`
  font-family: 'M PLUS Rounded 1c', sans-serif;
  font-size: 1.4rem;
  font-weight: bold;
  color: #E94E77;
  writing-mode: vertical-rl;
  text-orientation: mixed;
  text-shadow: 
    -1.5px -1.5px 0 #FFF,  
     1.5px -1.5px 0 #FFF,
    -1.5px  1.5px 0 #FFF,
     1.5px  1.5px 0 #FFF,
    2px 2px 4px rgba(0, 0, 0, 0.2);
  background: linear-gradient(to bottom, rgba(255, 255, 255, 0.98), rgba(255, 243, 246, 0.98));
  padding: 24px 16px;
  border-radius: 20px;
  box-shadow: 
    0 4px 16px rgba(233, 78, 119, 0.1),
    0 1px 3px rgba(233, 78, 119, 0.05),
    inset 0 0 0 1px rgba(255, 183, 197, 0.2);
  letter-spacing: 2px;
  order: 1;
  margin-right: 50px;
  backdrop-filter: blur(8px);
  transition: all 0.3s ease;

  &:hover {
    box-shadow: 
      0 6px 20px rgba(233, 78, 119, 0.15),
      0 2px 4px rgba(233, 78, 119, 0.08),
      inset 0 0 0 1.5px rgba(255, 183, 197, 0.3);
    transform: translateY(-1px);
  }
`;

const StyledLink = styled(Link)`
  text-decoration: none;
  display: flex;
  align-items: center;
  gap: 12px;
`;

export function MainLayout() {
  const { account } = useAccount();
  const { setVisible } = useWalletModal();

  return (
    <>
      <Background />
      <TopBar>
        <Logo src="/asset/lily.png" alt="Lily Logo" />
        <TopBarRight>
          <SocialIconsGroup>
            <SocialLinks hideDiscord />
          </SocialIconsGroup>
          {!account ? (
            <WalletLink onClick={() => setVisible(true)}>Connect Wallet</WalletLink>
          ) : (
            <Navigation />
          )}
        </TopBarRight>
      </TopBar>
      <LayoutContainer>
        <InteractiveSection>
          {!account ? (
            <WelcomeMessage>
              Konnichiwa! <span>✿</span> I'm Lily, your guide to the Web3 world! <span>✨</span><br />
              Connect your{' '}
              <WalletLink onClick={() => setVisible(true)}>Solana wallet</WalletLink>
              {' '}and we'll begin our adventure! Won't cost you a dime!
            </WelcomeMessage>
          ) : (
            <Chat />
          )}
        </InteractiveSection>
        <CharacterArea>
          <CharacterSection />
          <HeroPointer>
            <StyledLink to="/pulse">
              <HeroText>Meet the hero behind Lily</HeroText>
              <ArrowImage src="/asset/arrow.png" alt="Arrow pointing to Lily" />
            </StyledLink>
          </HeroPointer>
        </CharacterArea>
      </LayoutContainer>
    </>
  );
} 